import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  Mail, Send, RefreshCcw, CheckCircle2, XCircle, Clock,
  Award, Sparkles, ArrowLeft, AlertTriangle, BookOpen,
} from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  receipt: { label: "Receipt", icon: Mail, color: "text-zinc-700" },
  certificate: { label: "Certificate", icon: Award, color: "text-red-600" },
  foundation_upsell: { label: "Upsell", icon: Sparkles, color: "text-amber-600" },
};

const STATUS_CLASS = {
  sent: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-700",
  error: "bg-red-100 text-red-700",
  mocked: "bg-zinc-200 text-zinc-700",
};

export default function InstructorEmailLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("all"); // all | sent | failed | mocked
  const [testForm, setTestForm] = useState({ to: "", kind: "receipt" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-logs?limit=100");
      setLogs(data.logs || []);
    } catch {
      toast.error("Could not load email logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!testForm.to.trim()) {
      toast.error("Enter a recipient email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/test-email", testForm);
      const status = data.log?.status || "queued";
      if (status === "sent") {
        toast.success(`Sent — provider id ${data.log?.provider_id?.slice(0, 8)}…`);
      } else if (status === "mocked") {
        toast.warning("Email mocked — RESEND_API_KEY is not set");
      } else {
        toast.error(`Send ${status}: ${data.log?.error || "see log"}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return logs;
    if (filter === "failed") return logs.filter((l) => l.status === "failed" || l.status === "error");
    return logs.filter((l) => l.status === filter);
  }, [logs, filter]);

  const stats = useMemo(() => {
    const s = { total: logs.length, sent: 0, failed: 0, mocked: 0 };
    for (const l of logs) {
      if (l.status === "sent") s.sent++;
      else if (l.status === "failed" || l.status === "error") s.failed++;
      else if (l.status === "mocked") s.mocked++;
    }
    return s;
  }, [logs]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="instructor-email-logs-page">
        <Link to="/instructor" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="email-logs-back">
          <ArrowLeft className="w-4 h-4" /> Cohort
        </Link>
        <div className="overline mb-2"><span className="red-bar" />Operations</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-2" data-testid="email-logs-title">
          Email history
        </h1>
        <p className="text-zinc-600 max-w-2xl mb-8">
          Every receipt, certificate, and upsell email sent via Resend. Mocked entries mean the
          API key was missing at send time.
        </p>

        {/* Stat cards */}
        <div className="grid sm:grid-cols-4 gap-3 mb-8" data-testid="email-logs-stats">
          {[
            { label: "Total", value: stats.total, icon: Mail },
            { label: "Sent", value: stats.sent, icon: CheckCircle2, cls: "text-green-600" },
            { label: "Failed / Error", value: stats.failed, icon: XCircle, cls: "text-red-600" },
            { label: "Mocked", value: stats.mocked, icon: AlertTriangle, cls: "text-amber-600" },
          ].map((s) => (
            <div key={s.label} className="card-tech p-5">
              <div className="flex items-center gap-3 mb-2">
                <s.icon className={`w-4 h-4 ${s.cls || "text-zinc-500"}`} strokeWidth={2.25} />
                <div className="overline"><span className="red-bar" />{s.label}</div>
              </div>
              <div className="font-heading text-3xl font-bold tracking-tight">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Send test panel */}
        <div className="card-tech p-6 mb-10" data-testid="email-logs-test-panel">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-red-600" />
            <div className="overline"><span className="red-bar" />Send a test email</div>
          </div>
          <div className="grid sm:grid-cols-[1fr_220px_auto] gap-3">
            <input
              type="email"
              value={testForm.to}
              onChange={(e) => setTestForm({ ...testForm, to: e.target.value })}
              placeholder="recipient@example.com"
              className="border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 font-mono text-sm"
              data-testid="email-logs-test-to"
            />
            <select
              value={testForm.kind}
              onChange={(e) => setTestForm({ ...testForm, kind: e.target.value })}
              className="border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 font-mono text-sm bg-white"
              data-testid="email-logs-test-kind"
            >
              <option value="receipt">Receipt</option>
              <option value="certificate">Certificate-earned</option>
              <option value="upsell">Foundation upsell</option>
            </select>
            <button
              onClick={sendTest}
              disabled={sending}
              className="btn-primary disabled:opacity-50"
              data-testid="email-logs-test-send-btn"
            >
              <Send className="w-4 h-4" />
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
          <p className="text-xs text-zinc-500 font-mono mt-3">
            Sandbox note: Resend's default <code>onboarding@resend.dev</code> sender can only deliver to the account-owner email. Verify a domain at resend.com/domains and update <code>RESEND_FROM</code> in <code>/app/backend/.env</code> to send to anyone.
          </p>
        </div>

        {/* Filters + refresh */}
        <div className="flex items-center gap-2 mb-5">
          <div className="flex gap-1 border-2 border-zinc-200 p-1 bg-white">
            {[
              { id: "all", label: "All" },
              { id: "sent", label: "Sent" },
              { id: "failed", label: "Failed" },
              { id: "mocked", label: "Mocked" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                  filter === f.id ? "bg-red-600 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`}
                data-testid={`email-logs-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button onClick={load} className="btn-outline" data-testid="email-logs-refresh">
            <RefreshCcw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-zinc-500 font-mono">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="card-tech p-12 text-center text-zinc-500" data-testid="email-logs-empty">
            <BookOpen className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
            <p className="font-heading font-bold mb-1">No emails match</p>
            <p className="text-sm">Send a test above, or adjust the filter.</p>
          </div>
        ) : (
          <div className="border-2 border-zinc-200 overflow-x-auto" data-testid="email-logs-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-950 text-white text-left font-mono text-[11px] uppercase tracking-widest">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Kind</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Subject</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Detail</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => {
                  const k = KIND_LABEL[l.kind] || { label: l.kind || "email", icon: Mail, color: "text-zinc-700" };
                  const KIcon = k.icon;
                  return (
                    <tr key={l.id} className="border-t border-zinc-100 hover:bg-red-50/30" data-testid={`email-log-row-${l.id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-600 whitespace-nowrap">
                        <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(l.created_at).toLocaleString()}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 font-mono text-xs ${k.color}`}>
                          <KIcon className="w-3.5 h-3.5" />
                          {k.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-700">{l.to}</td>
                      <td className="px-4 py-3 text-zinc-800 max-w-xs truncate" title={l.subject}>{l.subject}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-1 ${STATUS_CLASS[l.status] || "bg-zinc-100 text-zinc-600"}`}>
                          {l.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-[11px] text-zinc-600 max-w-[260px]">
                        {l.provider_id && <div className="truncate" title={l.provider_id}>id: {l.provider_id.slice(0, 14)}…</div>}
                        {l.error && <div className="text-red-600 truncate" title={l.error}>err: {l.error.slice(0, 80)}…</div>}
                        {!l.provider_id && !l.error && <span className="text-zinc-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
